/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Search, filter, and print handlers for ViewAppointments.
 * @module pages/viewAppointments/useViewAppointmentsFilterHandlers
 */

import { useCallback } from "react";
import { logger, sanitizeError } from "../../utils/logger.js";
import { exportCalendarToPdf } from "../../utils/appointments/calendarPdfExport.js";
import {
  calculateDropdownPosition,
  calculateFiltersDropdownPosition,
} from "../../utils/ui/dropdownPosition.js";
import useViewAppointmentsBuyerHandlers from "./useViewAppointmentsBuyerHandlers.js";

/**
 * @param {Object} ctx - ViewAppointments context
 * @param {Object} fetching - Fetch helpers
 * @returns {Object} Filter and dropdown toggle handlers
 */
const useViewAppointmentsFilterHandlers = (ctx, fetching) => {
  const {
    t,
    eventData,
    userData,
    selectedBuyerCompany,
    selectedBuyerExecutive,
    selectedRequirement,
    participatingCompanyData,
    isFiltersDropdownOpen,
    activeDropdownId,
    setSearchQuery,
    setSelectedRequirement,
    setCurrentPage,
    setIsLoading,
    setSelectedStatusFilter,
    setSelectedRequirementsFilter,
    setIsFiltersDropdownOpen,
    setFiltersDropdownPosition,
    setFiltersDropdownDirection,
    setActiveDropdownId,
    setDropdownPosition,
    setDropdownDirection,
  } = ctx;

  const buyerHandlers = useViewAppointmentsBuyerHandlers(ctx, fetching);

  const handleSearch = useCallback((event) => {
    setSearchQuery(event.target.value);
  }, [setSearchQuery]);

  const handleRequirementChange = useCallback(
    (selectedOption) => {
      setSelectedRequirement(selectedOption);
      setCurrentPage(1);
    },
    [setSelectedRequirement, setCurrentPage],
  );

  const handleClearFilters = useCallback(() => {
    setSelectedStatusFilter(null);
    setSelectedRequirementsFilter(null);
    setCurrentPage(1);
  }, [setSelectedStatusFilter, setSelectedRequirementsFilter, setCurrentPage]);

  const toggleFiltersDropdown = useCallback(
    (event) => {
      event.stopPropagation();

      if (isFiltersDropdownOpen) {
        setIsFiltersDropdownOpen(false);
      } else {
        const button = event.currentTarget;
        const rect = button.getBoundingClientRect();
        const position = calculateFiltersDropdownPosition(rect);

        setFiltersDropdownPosition({
          top: position.top,
          left: position.left,
        });
        setFiltersDropdownDirection(position.shouldOpenAbove ? "above" : "below");
        setIsFiltersDropdownOpen(true);
      }
    },
    [
      isFiltersDropdownOpen,
      setIsFiltersDropdownOpen,
      setFiltersDropdownPosition,
      setFiltersDropdownDirection,
    ],
  );

  const toggleDropdown = useCallback(
    (requestId, event) => {
      event.stopPropagation();

      if (activeDropdownId === requestId) {
        setActiveDropdownId(null);
      } else {
        const button = event.currentTarget;
        const rect = button.getBoundingClientRect();
        const position = calculateDropdownPosition(rect);

        setDropdownPosition({
          top: position.top,
          left: position.left,
        });
        setDropdownDirection(position.shouldOpenAbove ? "above" : "below");
        setActiveDropdownId(requestId);
      }
    },
    [
      activeDropdownId,
      setActiveDropdownId,
      setDropdownPosition,
      setDropdownDirection,
    ],
  );

  const handlePrintCalendar = useCallback(async () => {
    try {
      setIsLoading(true);
      await exportCalendarToPdf({
        eventData,
        userData,
        selectedBuyerCompany,
        selectedBuyerExecutive,
        selectedRequirement,
        participatingCompanyData,
        t,
      });
    } catch (err) {
      logger.error("Error printing calendar:", { err: sanitizeError(err) });
    } finally {
      setIsLoading(false);
    }
  }, [
    setIsLoading,
    eventData,
    userData,
    selectedBuyerCompany,
    selectedBuyerExecutive,
    selectedRequirement,
    participatingCompanyData,
    t,
  ]);

  return {
    handleSearch,
    handleRequirementChange,
    handleClearFilters,
    toggleFiltersDropdown,
    toggleDropdown,
    handlePrintCalendar,
    ...buyerHandlers,
  };
};

export default useViewAppointmentsFilterHandlers;
